/**
 * @module routes.FacilityUsers.applyUserEdit
 *
 * The list after a member has been edited.
 *
 * CFTB-3447: the edited fields are laid *over* the member rather than replacing
 * them. The row used to become the modal's payload, which carries only what the
 * form collects — so everything else the list holds went with it, the member's
 * `avatar` most visibly: their picture turned into the CoachNow logo the row falls
 * back to, until the page was reloaded.
 *
 * The response cannot supply the missing fields either: the memberships PUT answers
 * 204 with no body (`responders` renders `head :no_content` for anything that is not
 * a GET or a POST), which is why nothing reads it.
 */

interface EditedUser {
  id: string;
  role?: string;
  isAdmin?: boolean;
}

/**
 * "coach - admin" is how this list labels a coach who is also an admin; the form
 * sends the plain role and the flag beside it.
 */
const roleLabel = (edited: EditedUser): string | undefined =>
  edited.role === "coach" && edited.isAdmin ? "coach - admin" : edited.role;

export const applyUserEdit = <T extends { id: string }>(
  users: T[],
  edited: EditedUser,
): T[] =>
  (users || []).map((user) =>
    user.id === edited.id
      ? ({ ...user, ...edited, role: roleLabel(edited) } as unknown as T)
      : user,
  );

export default applyUserEdit;
